import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import moment from 'moment';

import Tab from 'components/common/Tab';
import { CommonDateRange, CustomSelect } from 'components';
import { constant } from 'constants/constant';
import { getGpsTimeline, getGpsVehicle } from '_services';
import CommonDateTimeRange from 'components/common/CommonDateTimeRange';
import { FaPlay, FaStop } from 'react-icons/fa6';
import StorageService from 'utils/StorageServices';
import RunningVehicleGps from './RunningVehicleGps';
import Loading from 'pages/Loading';
import { Images } from 'constants';

const useQuery = () => {
    return new URLSearchParams(useLocation().search);
};

const GpsTimeline = () => {
    const query = useQuery();
    const navigate = useNavigate();
    const location = useLocation();

    // Query params
    const tbn = query.get('tbn') || '1';
    const vn = query.get('vn') || '';
    const cmid = query.get('cmid') || '';

    const [isLoading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState(Number(tbn));
    const [timeline, setTimeline] = useState([]);
    const [vehicle, setVehicle] = useState([]);
    const [allVehicles, setVehicles] = useState([]);
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [selectedDateTimeRange, setSelectedDateTimeRange] = useState({
        startDate: moment().subtract(6, 'days').startOf('day').add(1, 'minute'),
        endDate: moment().endOf('day'),
    });

    // Handle tab changes
    const handleTabChange = (tabId) => {
        setActiveTab(tabId);
        const params = new URLSearchParams(location.search);
        params.set('tbn', tabId);
        navigate(`${location.pathname}?${params.toString()}`);
    };

    // Fetch GPS timeline
    const fetchGpsTimeline = async () => {
        if (!cmid || !vn) return;

        const startDate = selectedDateTimeRange?.startDate
            ? moment(selectedDateTimeRange.startDate).startOf('day').add(1, 'minute')
            : moment().subtract(6, 'days').startOf('day').add(1, 'minute'); // 7-day range includes today

        const endDate = selectedDateTimeRange?.endDate
            ? moment(selectedDateTimeRange.endDate).endOf('day')
            : moment().endOf('day'); // today's end of day


        const payload = {
            company_id: cmid,
            vehicle_no: vn,
            // startDate: selectedDateTimeRange?.startDate
            //     ? moment(selectedDateTimeRange.startDate).format('YYYY-MM-DDTHH:mm:ssZ')
            //     : moment().format('YYYY-MM-DDTHH:mm:ssZ'),
            // endDate: selectedDateTimeRange?.endDate
            //     ? moment(selectedDateTimeRange.endDate).format('YYYY-MM-DDTHH:mm:ssZ')
            //     : moment().format('YYYY-MM-DDTHH:mm:ssZ'),
            start_date: startDate.format('YYYY-MM-DDTHH:mm:ssZ'),
            end_date: endDate.format('YYYY-MM-DDTHH:mm:ssZ'),
            type: activeTab,
        };

        try {
            setLoading(true)
            const res = await getGpsTimeline(payload);
            if (res?.responseCode === 200) {
                setTimeline(res?.data?.timeline || []);
                setVehicle(res?.data?.vehicle || []);
            }
        } catch (error) {
            console.error('Error fetching GPS timeline', error);
        }
        finally {
            setLoading(false)
        }
    };

    // Fetch vehicle list
    const fetchGpsVehicle = async () => {
        const payload = {
            company_id: cmid || StorageService.getCompanyId(),
            limit: 100,
            offset: 1,
            search: ''
        };

        try {
            const res = await getGpsVehicle(payload);
            if (res?.responseCode === 200) {
                setVehicles(res?.data?.vehicles || []);
            }
        } catch (error) {
            console.error('Error fetching GPS vehicles', error);
        }
    };

    // Trigger timeline fetch
    useEffect(() => {
        if (cmid && vn) {
            fetchGpsTimeline();
        }
    }, [cmid, vn, selectedDateTimeRange.startDate, selectedDateTimeRange.endDate, activeTab]);

    // Trigger vehicle fetch on mount
    useEffect(() => {
        fetchGpsVehicle();
    }, []);

    // Sync vehicle dropdown with query params
    useEffect(() => {
        if (vn && cmid && allVehicles.length) {
            const matched = allVehicles.find(v => v.vehicle_no === vn);
            setSelectedVehicle(matched ? {
                label: matched.vehicle_no,
                value: matched.vehicle_no,
                cmid: matched.company_id
            } : {
                label: vn,
                value: vn,
                cmid
            });
        }
    }, [vn, cmid, allVehicles]);

    // Tab prop config
    const tabProps = {
        tabList: constant.gpsTrackerTabs,
        callBackTab: handleTabChange,
        activeTab
    };

    // On vehicle select
    const handleVehicleSelect = (item) => {
        setSelectedVehicle(item);
        const params = new URLSearchParams(location.search);
        params.set('vn', item?.value);
        params.set('cmid', item?.cmid);
        navigate(`${location.pathname}?${params.toString()}`);
    };

    // On date range apply
    const handleApplyDateRange = (range) => {
        setSelectedDateTimeRange(range)
        // setSelectedDateTimeRange({
        //     startDate: range.startDate,
        //     endDate: range.endDate,
        // });
    };

    // Dropdown options
    const vehicleOptions = useMemo(() => (
        allVehicles.map((v) => ({
            label: v.vehicle_no,
            value: v.vehicle_no,
            cmid: v.company_id
        }))
    ), [allVehicles]);

    return (
        <div className="__mainGps_bx">
            <div className="addPr_flBx">
                <div className="_secFilts_gps" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Tab {...tabProps} />
                    <div className="llt_dash_branch_date">
                        {/* <CommonDateTimeRange onApply={handleApplyDateRange} /> */}

                        <div className="__gpsDateRange">
                            <CommonDateRange
                                initialStartDate={selectedDateTimeRange?.start_date}
                                initialEndDate={selectedDateTimeRange?.end_date}
                                onApply={handleApplyDateRange} />
                        </div>

                        <div className="__gpsVehicleSelect">
                            <CustomSelect
                                options={vehicleOptions}
                                name="vehicle_no"
                                label="Select Vehicle"
                                placeholder="Select Vehicle"
                                value={selectedVehicle}
                                onSelect={handleVehicleSelect}
                            />
                        </div>
                    </div>
                </div>

                {activeTab == 0 &&
                    <div className='__gpsSummary_bx'>
                        <div className='__gpsSummary_inr __gpsSummary_runningTab'>
                            <div className='__gpsSummary_item'>
                                <table>
                                    <thead>
                                        <tr>
                                            <td><div className='__txt_sphire'>Running Type</div></td>
                                            <td><div className='__txt_sphire'>Vehicle Avg</div></td>
                                            <td><div className='__txt_sphire'>KM Running</div></td>
                                            <td><div className='__txt_sphire'>GPS Fuel Consumed</div></td>
                                            <td><div className='__txt_sphire'>Running Duration</div></td>
                                            <td><div className='__txt_sphire'>Stopped Duration</div></td>
                                            <td><div className='__txt_sphire'>Ideal Duration</div></td>
                                            <td><div className='__txt_sphire'>GPS Inactive</div></td>
                                            <td><div className='__txt_sphire'>Alerts</div></td>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        <tr>
                                            <td><div className='__txt_sphire'>On Trip Running</div></td>
                                            <td><div className='__txt_sphire'>4.5</div></td>
                                            <td><div className='__perCent_bxx'><div className='__txt_sphire'>5000 </div><span style={{ backgroundColor: 'rgba(242, 255, 239, 1)' }}>80.64%</span></div></td>
                                            <td><div className='__txt_sphire'>1111.11 Ltr</div></td>
                                            <td><div className='__perCent_bxx'><div className='__txt_sphire'>20D | 3H | 40M</div><span style={{ backgroundColor: 'rgba(242, 255, 239, 1)' }}>80%</span></div></td>
                                            <td><div className='__txt_sphire'>1D | 3H | 40M</div></td>
                                            <td><div className='__txt_sphire'>0D | 3H | 40M</div></td>
                                            <td><div className='__txt_sphire'>0D | 3H | 40M</div></td>
                                            <td><div className='__txt_sphire'>8</div></td>
                                        </tr>
                                    </tbody>

                                    <tfoot>
                                        <tr>
                                            <td><div className='__txt_sphire'>Total Running</div></td>
                                            <td><div className='__txt_sphire'>4.75</div></td>
                                            <td><div className='__txt_sphire'>6200</div></td>
                                            <td><div className='__txt_sphire'>240 Ltr</div></td>
                                            <td><div className='__txt_sphire'>21D | 3H | 40M</div></td>
                                            <td><div className='__txt_sphire'>2D | 3H | 40M</div></td>
                                            <td><div className='__txt_sphire'>0D | 3H | 40M</div></td>
                                            <td><div className='__txt_sphire'>0D | 3H | 40M</div></td>
                                            <td><div className='__txt_sphire'>10</div></td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>

                        <div className='__gpsSummary_inr __gpsSummary_alertTab'>
                            <div className='__gpsSummary_item'>
                                <div className='__alertHead'>Alerts (10)</div>

                                <div className=' __alertFilter'>
                                    <button className='__alrtActive'>All Alerts 10</button>
                                    <button>Speed Alert (1)</button>
                                    <button>Ideal Alert (1)</button>
                                    <button>Breaking (1)</button>
                                    <button>GPS Inactive (1)</button>
                                    <button>Stoppage (1)</button>
                                    <button>Geofence (2)</button>
                                </div>

                                <table>
                                    <thead>
                                        <tr>
                                            <td><div className='__txt_sphire'>Alert Type</div></td>
                                            <td><div className='__txt_sphire'>Date and Time</div></td>
                                            <td><div className='__txt_sphire'>Vehicle Number</div></td>
                                            <td><div className='__txt_sphire'>Driver</div></td>
                                            <td><div className='__txt_sphire'>Alert Message</div></td>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        <tr>
                                            <td><div class='__txt_sphire'>Speed Alert</div></td>
                                            <td><div class='__txt_sphire'>23 June 2025 | 4:30PM</div></td>
                                            <td><div class='__txt_sphire'>MP20GA2109</div></td>
                                            <td><div class='__txt_sphire'>Ram Kumar Yadav</div></td>
                                            <td><div class='__txt_sphire'>Vehicle Running at Speed of more than {`set Limit`}</div></td>
                                        </tr>
                                        <tr>
                                            <td><div class='__txt_sphire'>Harsh Breaking</div></td>
                                            <td><div class='__txt_sphire'>23 June 2025 | 4:30PM</div></td>
                                            <td><div class='__txt_sphire'>MP20GA2109</div></td>
                                            <td><div class='__txt_sphire'>Ram Kumar Yadav</div></td>
                                            <td><div class='__txt_sphire'>Speed Suddenly fall From 60KM to 10KM/Hour</div></td>
                                        </tr>
                                        <tr>
                                            <td><div class='__txt_sphire'>Ideal Duration</div></td>
                                            <td><div class='__txt_sphire'>23 June 2025 | 4:30PM</div></td>
                                            <td><div class='__txt_sphire'>MP20GA2109</div></td>
                                            <td><div class='__txt_sphire'>Ram Kumar Yadav</div></td>
                                            <td><div class='__txt_sphire'>Vehicle Engine is started from last {`set timer`}</div></td>
                                        </tr>
                                        <tr>
                                            <td><div class='__txt_sphire'>GPS Inactive</div></td>
                                            <td><div class='__txt_sphire'>23 June 2025 | 4:30PM</div></td>
                                            <td><div class='__txt_sphire'>MP20GA2109</div></td>
                                            <td><div class='__txt_sphire'>Ram Kumar Yadav</div></td>
                                            <td><div class='__txt_sphire'>Vehicle GPS is Inactive from last {`set time`}</div></td>
                                        </tr>
                                        <tr>
                                            <td><div class='__txt_sphire'>Stoppage</div></td>
                                            <td><div class='__txt_sphire'>23 June 2025 | 4:30PM</div></td>
                                            <td><div class='__txt_sphire'>MP20GA2109</div></td>
                                            <td><div class='__txt_sphire'>Ram Kumar Yadav</div></td>
                                            <td><div class='__txt_sphire'>Vehicle is on Trip and Stopped from more than {`set Time`}</div></td>
                                        </tr>
                                        <tr>
                                            <td><div class='__txt_sphire'>Stoppage</div></td>
                                            <td><div class='__txt_sphire'>23 June 2025 | 4:30PM</div></td>
                                            <td><div class='__txt_sphire'>MP20GA2109</div></td>
                                            <td><div class='__txt_sphire'>Ram Kumar Yadav</div></td>
                                            <td><div class='__txt_sphire'>Vehicle is Running Empty and Stopped from more than {`set Time`}</div></td>
                                        </tr>
                                        <tr>
                                            <td><div class='__txt_sphire'>Geofence In</div></td>
                                            <td><div class='__txt_sphire'>23 June 2025 | 4:30PM</div></td>
                                            <td><div class='__txt_sphire'>MP20GA2109</div></td>
                                            <td><div class='__txt_sphire'>Ram Kumar Yadav</div></td>
                                            <td><div class='__txt_sphire'>Vehicle is Entered Geofence zone {`MP Agency, Jalgaon`}</div></td>
                                        </tr>
                                        <tr>
                                            <td><div class='__txt_sphire'>Geofence Out</div></td>
                                            <td><div class='__txt_sphire'>23 June 2025 | 4:30PM</div></td>
                                            <td><div class='__txt_sphire'>MP20GA2109</div></td>
                                            <td><div class='__txt_sphire'>Ram Kumar Yadav</div></td>
                                            <td><div class='__txt_sphire'>Vehicle is out Geofence zone {`MP Agency, Jalgaon`}</div></td>
                                        </tr>
                                    </tbody>

                                </table>
                            </div>
                        </div>
                    </div>
                }

                {activeTab == 1 &&
                    <div className="__fltTier_tableBx">
                        <table>
                            <thead>
                                <tr>
                                    <th>Vehicle</th>
                                    <th>Start Time</th>
                                    <th>End Time</th>
                                    <th>Start Location</th>
                                    <th>End Location</th>
                                    <th>Distance</th>
                                    <th>Duration</th>
                                    <th>Mileage</th>
                                    <th>Fuel</th>
                                    <th>Running Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ?
                                    <tr>
                                        <td colSpan={10}><div className='__fltHist_txs' style={{ textAlign: 'center' }}>Loading...</div></td>
                                    </tr>
                                    :
                                    timeline.length > 0 ? (
                                        timeline.map((item, index) => {
                                            const itemTimeline = item?.timeline;
                                            // let durationText = '';

                                            // if (itemTimeline?.length >= 2) {
                                            // const startEpoch = itemTimeline[0]?.dttimeInEpoch;
                                            // const endEpoch = itemTimeline[itemTimeline.length - 1]?.dttimeInEpoch;

                                            // const startEpoch = item?.startdttimeInEpoch;
                                            // const endEpoch = item?.enddttimeInEpoch;

                                            // const totalSeconds = Math.floor((endEpoch - startEpoch) / 1000);
                                            // const totalMinutes = Math.floor(totalSeconds / 60);
                                            // const hours = Math.floor(totalMinutes / 60);
                                            // const minutes = totalMinutes % 60;

                                            // durationText = `${hours}h ${minutes}m`;
                                            // }

                                            let durationText = '-';
                                            if (item.startdttimeInEpoch && item.enddttimeInEpoch) {
                                                const totalSeconds = Math.floor((item.enddttimeInEpoch - item.startdttimeInEpoch) / 1000);
                                                const totalMinutes = Math.floor(totalSeconds / 60);
                                                const hours = Math.floor(totalMinutes / 60);
                                                const minutes = totalMinutes % 60;

                                                // ✅ Show only hours if > 0
                                                durationText = hours > 0
                                                    ? `${hours}h ${minutes}m`
                                                    : `${minutes}m`;
                                            }

                                            return (
                                                <tr key={index}>
                                                    <td><div className='__fltHist_txs'>{item?.vehicleNumber}</div></td>
                                                    <td>
                                                        <div className='__fltHist_txs'>{moment(item?.startdttimeInEpoch).format('DD MMM yyyy')}</div>
                                                        <div className='__fltHist_txs'>{moment(item?.startdttimeInEpoch).format('hh:mm A')}</div>
                                                    </td>
                                                    <td>
                                                        <div className='__fltHist_txs'>{moment(item?.enddttimeInEpoch).format('DD MMM yyyy')}</div>
                                                        <div className='__fltHist_txs'>{moment(item?.enddttimeInEpoch).format('hh:mm A')}</div>
                                                    </td>
                                                    <td><div className='__fltHist_txs'>{itemTimeline?.[0]?.address || '-'}</div></td>
                                                    <td><div className='__fltHist_txs'>{itemTimeline?.[itemTimeline.length - 1]?.address || '-'}</div></td>
                                                    <td><div className='__fltHist_txs'>{`${Number(item?.kms).toFixed(2)} KM` ?? '-'}</div></td>
                                                    <td><div className='__fltHist_txs'>{durationText}</div></td>
                                                    <td><div className='__fltHist_txs'>{vehicle?.vehicle_average ?? '-'}</div></td>
                                                    <td><div className='__fltHist_txs'>{vehicle?.average_fuel_rate ?? '-'}</div></td>
                                                    <td>
                                                        {item?.ontrip == 1 ?
                                                            <div className='__onTrip_txs_main'>
                                                                <span className='__onTrip_num_txs'>{item?.tripid}</span>
                                                                <span className='__onTrip_txs'>OnTrip</span>
                                                            </div>
                                                            :
                                                            <div className='__empty_txs'>
                                                                Empty
                                                            </div>
                                                        }
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan={10} style={{ textAlign: 'center' }}>
                                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                                    <img src={Images.NoRecordFound} alt="" />
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                            </tbody>
                        </table>
                    </div>}

                {activeTab === 2 && (
                    <div className='__fltTierBx'>
                        <div className='__flxTierDx'>
                            <div className='__gpsTimeLine_bx'>

                                {isLoading ?
                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                        <Loading />
                                    </div>
                                    : timeline.length > 0 ? (
                                        timeline.map((item, index) => {
                                            const itemTimeline = item?.timeline;
                                            return (<div className='__fltsFlects_main'>
                                                <div className='__fltsFlects_time'>
                                                    <div>
                                                        <h4>{moment(item.startdttimeInEpoch).format("hh:mm A")}</h4>
                                                        <p>{moment(item.startdttimeInEpoch).format("DD/MM/YYYY")}</p>
                                                    </div>

                                                    <div>
                                                        <h4>{moment(item.enddttimeInEpoch).format("hh:mm A")}</h4>
                                                        <p>{moment(item.enddttimeInEpoch).format("DD/MM/YYYY")}</p>
                                                    </div>
                                                </div>
                                                <div className={`__fltsFlects_detail ${item.ignition ? '__fltsFlects_running' : '__fltsFlects_stop'}`}>
                                                    <span className='__verLine_flt'></span>

                                                    <div className='__fltsDetail_inr'>
                                                        <h4>{item.ignition ? "Running" : "Stop"}</h4>
                                                        <h5>1m | Ideal 0Min</h5>
                                                        <p>{itemTimeline?.[0]?.address || '-'}</p>
                                                    </div>
                                                </div>
                                            </div>
                                            );
                                        })
                                    ) : (
                                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                            <img src={Images.NoRecordFound} alt="" />
                                        </div>
                                    )}
                            </div>

                            <RunningVehicleGps timeline={timeline} />

                        </div>
                    </div>
                )}

                {activeTab == 3 &&
                    <div className="__fltTier_tableBx">
                        <table>
                            <thead>
                                <tr>
                                    <th>Vehicle</th>
                                    <th>Stop Time</th>
                                    <th>Start Time</th>
                                    <th>Location</th>
                                    <th>Nearest Location</th>
                                    <th>After Distance</th>
                                    <th>Duration</th>
                                    <th>Running Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ?
                                    <tr>
                                        <td colSpan={10}><div className='__fltHist_txs' style={{ textAlign: 'center' }}>Loading...</div></td>
                                    </tr>
                                    : timeline.length > 0 ? (
                                        timeline.map((item, index) => {
                                            const itemTimeline = item?.timeline;


                                            return (
                                                <tr key={index}>
                                                    <td><div className='__fltHist_txs'>{item?.vehicleNumber}</div></td>
                                                    <td>
                                                        <div className='__fltHist_txs'>{moment(item?.enddttimeInEpoch).format('DD MMM yyyy')}</div>
                                                        <div className='__fltHist_txs'>{moment(item?.enddttimeInEpoch).format('hh:mm A')}</div>
                                                    </td>
                                                    <td>
                                                        <div className='__fltHist_txs'>{moment(item?.startdttimeInEpoch).format('DD MMM yyyy')}</div>
                                                        <div className='__fltHist_txs'>{moment(item?.startdttimeInEpoch).format('hh:mm A')}</div>
                                                    </td>
                                                    <td>
                                                        <div className='__fltHist_txs'>{itemTimeline?.[0]?.address || '-'}</div>
                                                    </td>
                                                    <td><div className='__fltHist_txs'>-</div></td>
                                                    <td>
                                                        <div className='__fltHist_txs'>{`${Number(item?.kms).toFixed(2)} KM` ?? '-'}</div>
                                                    </td>
                                                    <td><div className='__fltHist_txs'>-</div></td>
                                                    <td>
                                                        {item?.ontrip == 1 ?
                                                            <div className='__onTrip_txs_main'>
                                                                <span className='__onTrip_num_txs'>{item?.tripid}</span>
                                                                <span className='__onTrip_txs'>OnTrip</span>
                                                            </div>
                                                            :
                                                            <div className='__empty_txs'>
                                                                Empty
                                                            </div>
                                                        }
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan={10} style={{ textAlign: 'center' }}>
                                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                                    <img src={Images.NoRecordFound} alt="" />
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                            </tbody>
                        </table>
                    </div>}


            </div>
        </div>
    );
};

export default GpsTimeline;
